import * as React from 'react';

import { useCookies } from 'react-cookie';
import { connect } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { AuthorizedWindowWrapper } from './components/AuthorizedWindowWrapper';
import { AuthTimedOut } from './components/AuthTimedOut';
import { BasicStrongboxContentContainer } from './components/BasicStrongboxContentContainer/BasicStrongboxContentContainer';
import { BorrowerPortalV2 } from './components/BorrowerPortal/BorrowerPortalV2';
import { Documents } from './components/BorrowerPortal/Documents';
import { DownloadUserCopy } from './components/BorrowerPortal/DownloadUserCopy';
import { EmailConfirm } from './components/BorrowerPortal/EmailConfirm';
import { Linked } from './components/BorrowerPortal/Linked';
import { PortalSubmissionV2 } from './components/BorrowerPortal/PortalSubmissionV2';
import { Progress } from './components/BorrowerPortal/Progress';
import { ShareableLinkDeleted } from './components/BorrowerPortal/ShareableLinkDeleted';
import { ShareableLinkExpired } from './components/BorrowerPortal/ShareableLinkExpired';
import { DataCollectionReport } from './components/DataCollectionReport/DataCollectionReport';
import { LenderHome } from './components/LenderHome/LenderHome';
import { LenderPortal } from './components/LenderPortal';
import { LoginContainer } from './components/Login/LoginContainer';
import { PageNotFound } from './components/PageNotFound';
import { PostLogin } from './components/PostLogin/PostLogin';
import { SettingsContainer } from './components/Settings/SettingsContainer';
import { SlidingTimeout } from './components/SlidingTimeout/SlidingTimeout';
import { SubmissionDetails } from './components/SubmissionDetails/SubmissionDetails';
import { TenantDisabled } from './components/TenantDisabled/TenantDisabled';
import { TenantSelection } from './components/TenantSelection';
import { UserSettingsContainer, UserSettingsSubPage } from './components/UserSettings/UserSettings';
import { WorkspaceDetails } from './components/WorkspaceDetails';
import { Wrapper } from './components/Wrapper';
import { InitializeAppInsights } from './Utils/Logging';
import { LoggingWrapper } from './Utils/LoggingWrapper';

import * as AppSettings from './Store/AppSettings';
import { GetBasicSettings, TenantActionCreators, TenantBasicSettings } from './Store/Tenant';
import { GetUserIsAuthenticated } from './Store/UIState';
import { actionCreators as UserActions } from './Store/User';

import './custom.css';
import { AppCookieData } from './Models/CookieData';
import { ApplicationState } from './Store';
import { IAppSettingsState } from './Store/AppSettings';
import { BorrowerActionCreators } from './Store/Borrower';
import { ImportHasStarted } from './Store/ImportFinancials';
import { actionCreators as dataCollectionParametersActions } from './Store/NewDataCollectionParameters';
import { PortalAssetsActionCreators } from './Store/PortalAssets';
import { GetTenantName } from './Store/Tenant';

import { AppDataCookieNames, pathConstants, routeConstants } from './Utils/Constants';
import { LogMessage, SeverityLevel } from './Utils/Logging';


interface PropArguments {
    redirectPath?: string;
    clearRedirectPath?: () => void;
}

interface ReduxStateProps {
    appSettings: IAppSettingsState | undefined;
    tenantSettings: TenantBasicSettings | undefined;
    importHasStarted: boolean;
    tenantName: string;
    userIsAuthenticated: boolean;
}

type ReduxDispatchProps =
    typeof AppSettings.actionCreators &
    typeof UserActions &
    typeof BorrowerActionCreators &
    typeof TenantActionCreators &
    typeof PortalAssetsActionCreators;

type ComponentProps = PropArguments & ReduxStateProps & ReduxDispatchProps;

const SubmissionDetailsWrapper: React.FC<{
    onPostLoginComplete: () => void,
    mainDivKey: string,
    hideFooter?: boolean
}> = (props): React.ReactElement => {
    return (
        <AuthorizedWindowWrapper mainDivKey={props.mainDivKey} onPostLoginComplete={props.onPostLoginComplete}>
            <LenderPortal
                hideFooter={props.hideFooter}
            >
                <SubmissionDetails />
            </LenderPortal>
        </AuthorizedWindowWrapper>
    );
}

const WorkspaceDetailsWrapper: React.FC<{
    onPostLoginComplete: () => void,
    mainDivKey: string,
}> = (props): React.ReactElement => {
    return (
        <AuthorizedWindowWrapper mainDivKey={props.mainDivKey} onPostLoginComplete={props.onPostLoginComplete}>
            <LenderPortal>
                <WorkspaceDetails />
            </LenderPortal>
        </AuthorizedWindowWrapper>
    );
}

function App(props: ComponentProps) {
    const location = window.location.pathname.toLowerCase();

    // use react-hook to register for changes to cookie carrying application settings.
    const [cookies] = useCookies(Object.values(AppDataCookieNames));

    // This will give us an array containing an object for each of the cookie names defined in AppDataCookieNames
    const allCookieAppData = Object.values(AppDataCookieNames).map(cookieName => cookies[cookieName]);

    let appData = {};

    // Now take each of the objects in allCookieAppData (an array) and spread those into a single object.
    allCookieAppData.forEach(cookieData => {
        appData = {
            ...appData,
            ...cookieData,
        };
    });

    const [mainDivKey, setMainDivKey] = React.useState<number>(1);

    const onPostLoginComplete = (): void => {
        setMainDivKey(mainDivKey + 1);
    }

    React.useEffect(() => {
        const cookieData = appData as AppCookieData;

        props.Initialize({ ...cookieData });

        // mainDivKey is the only dependency I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [mainDivKey])

    React.useEffect(() => {
        // Check isAuthenticated, we actually need a consumer id available when we make the call to GetBasicTenantSettings
        // and there are scenarios where one is not available until we are authenticated.
        if (props.userIsAuthenticated) {

            // Retrieve basic tenant settings information from back end.  GetBasicTenantSettings is
            // async action creator.  It'll update the store and things will become available so there's
            // no need to await.

            props.GetBasicTenantSettings();
            props.LoadAccountingPackageDefinitions();
            props.FetchTenantFeatureSwitches();
        }

        // isAuthenticated is all I want here.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [props.userIsAuthenticated])

    if (!!appData && props.appSettings && !props.appSettings.initialized) {
        // This is stuff we truly need to do before doing anything else, even prior to
        // executing useEffect, that's why we aren't doing it in React.useEffect with
        // dependency [] equivalent to componentDidMount.

        const cookieData = appData as AppCookieData;

        // If the current path is the submission page and we're not initialized then we should
        // clear the saved submission ID from the redux store. 
        if (!!location && (location === pathConstants.submission)) {
            props.SetBorrowerSubmissionId('');
        }

        if (!!location && (location === pathConstants.submissionSansFileUpload)) {
            cookieData.disableAdditionalDocUpload = true;
            cookieData.disableAnonymousDocUpload = true;   // The above takes precedence anyway but just for good measure.
        }

        props.Initialize({ ...cookieData });
        props.UsersInitialize(cookieData);

        props.PortalAssetsInitialize(cookieData.allowLanguageSelection);

        if (!!cookieData.appInsightsInstrumentationKey) {
            InitializeAppInsights(
                cookieData.disableAppInsights,
                cookieData.appInsightsInstrumentationKey,
                props.tenantName
            );
        }

        dataCollectionParametersActions.InitializeFromURL(window.location, cookieData);
    } else {
        // Else if we're on the starting page and the UIState has ImportHasStarted set true, that
        // only happens when they get to the linked page so they have successfully started an import.
        // In that event we can safely clear the submission ID because it's likely they backspaced here
        // from the completion page after starting an import as opposed to above where they might
        // have navigated to the docs page and backspaced back to start an import.
        if ((!!location) &&
            (location === pathConstants.submission) &&
            (props.importHasStarted)
        ) {
            props.SetBorrowerSubmissionId('');
        }
    }

    if (!!props.redirectPath) {
        props.clearRedirectPath && props.clearRedirectPath();
        LogMessage(
            'Processing redirect after login',
            SeverityLevel.Information,
            {
                path: props.redirectPath
            }
        );
        return (
            <BrowserRouter>
                <Wrapper>
                    <Navigate to={props.redirectPath} />
                </Wrapper>
            </BrowserRouter>
        );
    }

    // props.tenantSettings can be undefined during load while they're being retrieved from blob storage.
    if (!props.tenantSettings) {
        return (
            <></>
        );
    } else if (!!props.tenantSettings && !!props.tenantSettings.redirections) {
        const redirect = props.tenantSettings.redirections[location];
        if (!!redirect) {
            return (
                <BrowserRouter>
                    <Wrapper>
                        <Navigate to={redirect} />
                    </Wrapper>
                </BrowserRouter>
            );
        }
    }

    // FYI.  The <Switch> line below was formerly <Layout> from Layout.tsx.   That made the connect page
    // broken and this fixes that.

    return (
        <LoggingWrapper>
            <BrowserRouter>
                <Wrapper>
                    <Routes>
                        <Route
                            path={'/postlogin'}
                            element={
                                <LenderPortal
                                    hideMenu={true}
                                    hideLoggedInUser={true}
                                    hideHelp={true}
                                    isLoginPage={true}
                                    disableUIRefreshTiming={true}
                                >
                                    <PostLogin />
                                </LenderPortal>
                            }
                        />
                        <Route
                            path='/'
                            element={
                                <LenderPortal
                                    hideMenu={true}
                                    hideLoggedInUser={true}
                                    hideHelp={true}
                                    isLoginPage={true}
                                    disableUIRefreshTiming={true}
                                >
                                    <LoginContainer />
                                </LenderPortal>
                            }
                        />
                        <Route
                            path={routeConstants.login}
                            element={
                                <LenderPortal
                                    hideMenu={true}
                                    hideLoggedInUser={true}
                                    hideHelp={true}
                                    isLoginPage={true}
                                    disableUIRefreshTiming={true}
                                >
                                    <LoginContainer />
                                </LenderPortal>
                            }
                        />
                        <Route
                            path={'/businesses'}
                            element={
                                <Navigate to={pathConstants.home} />
                            }
                        />
                        <Route
                            path={'/home'}
                            element={
                                <Navigate to={pathConstants.home} />
                            }
                        />
                        <Route
                            path={routeConstants.home}
                            element={
                                <AuthorizedWindowWrapper
                                    mainDivKey={mainDivKey.toString()}
                                    onPostLoginComplete={onPostLoginComplete}
                                >
                                    <LenderPortal>
                                        <LenderHome />
                                    </LenderPortal>
                                </AuthorizedWindowWrapper>
                            }
                        />
                        <Route path={routeConstants.workspaceSubmissionDetails}>
                            <Route path={routeConstants.workspaceSubmissionDetailsOverview}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}/${routeConstants.submissionIdParam}`}
                                    element={
                                        <SubmissionDetailsWrapper
                                            mainDivKey={mainDivKey.toString()}
                                            onPostLoginComplete={onPostLoginComplete}
                                        />}
                                />
                            </Route>
                            <Route path={routeConstants.workspaceSubmissionDetailsFinancialStatements}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}/${routeConstants.submissionIdParam}`}
                                    element={
                                        <SubmissionDetailsWrapper
                                            mainDivKey={mainDivKey.toString()}
                                            onPostLoginComplete={onPostLoginComplete}
                                            hideFooter={true}
                                        />
                                    }
                                />
                            </Route>
                            <Route path={routeConstants.workspaceSubmissionDetailsRiskAnalysis}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}/${routeConstants.submissionIdParam}`}
                                    element={
                                        <SubmissionDetailsWrapper
                                            mainDivKey={mainDivKey.toString()}
                                            onPostLoginComplete={onPostLoginComplete}
                                        />
                                    }
                                />
                            </Route>
                        </Route>
                        <Route path={routeConstants.allSubmissionDetails}>
                            <Route path={routeConstants.allSubmissionDetailsOverview}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}/${routeConstants.submissionIdParam}`}
                                    element={
                                        <SubmissionDetailsWrapper
                                            mainDivKey={mainDivKey.toString()}
                                            onPostLoginComplete={onPostLoginComplete}
                                        />
                                    }
                                />
                            </Route>
                            <Route path={routeConstants.allSubmissionDetailsFinancialStatements}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}/${routeConstants.submissionIdParam}`}
                                    element={
                                        <SubmissionDetailsWrapper
                                            mainDivKey={mainDivKey.toString()}
                                            onPostLoginComplete={onPostLoginComplete}
                                        />
                                    }
                                />
                            </Route>
                            <Route path={routeConstants.allSubmissionDetailsRiskAnalysis}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}/${routeConstants.submissionIdParam}`}
                                    element={
                                        <SubmissionDetailsWrapper
                                            mainDivKey={mainDivKey.toString()}
                                            onPostLoginComplete={onPostLoginComplete}
                                        />
                                    }
                                />
                            </Route>
                        </Route>
                        <Route path={routeConstants.workspaceDetails}>
                            <Route path={routeConstants.workspaceDetailsSubmissions}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}`}
                                    element={<WorkspaceDetailsWrapper
                                        mainDivKey={mainDivKey.toString()}
                                        onPostLoginComplete={onPostLoginComplete}
                                    />}
                                />
                            </Route>
                            <Route path={routeConstants.workspaceDetailsConnections}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}`}
                                    element={<WorkspaceDetailsWrapper
                                        mainDivKey={mainDivKey.toString()}
                                        onPostLoginComplete={onPostLoginComplete}
                                    />}
                                />
                            </Route>
                            <Route path={routeConstants.workspaceDetailsUsers}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}`}
                                    element={<WorkspaceDetailsWrapper
                                        mainDivKey={mainDivKey.toString()}
                                        onPostLoginComplete={onPostLoginComplete}
                                    />}
                                />
                            </Route>
                            <Route path={routeConstants.workspaceDetailsSubmissionList}>
                                <Route
                                    path={`${routeConstants.workspaceIdParam}`}
                                    element={<WorkspaceDetailsWrapper
                                        mainDivKey={mainDivKey.toString()}
                                        onPostLoginComplete={onPostLoginComplete}
                                    />}
                                />
                            </Route>
                        </Route>
                        <Route
                            path={routeConstants.dataCollectionReport}
                            element={
                                <AuthorizedWindowWrapper
                                    securedOp={'CanViewGlobalSubmissions'}
                                    mainDivKey={mainDivKey.toString()}
                                    onPostLoginComplete={onPostLoginComplete}
                                >
                                    <LenderPortal>
                                        <DataCollectionReport />
                                    </LenderPortal>
                                </AuthorizedWindowWrapper>
                            }
                        />
                        <Route
                            path={routeConstants.authTimeout}
                            element={
                                <LenderPortal
                                    hideMenu={true}
                                    disableUIRefreshTiming={true}
                                >
                                    <AuthTimedOut />
                                </LenderPortal>
                            }
                        />
                        <Route
                            path={routeConstants.slidingWindowTimeout}
                            element={
                                <LenderPortal
                                    hideMenu={true}
                                    disableUIRefreshTiming={true}
                                >
                                    <SlidingTimeout />
                                </LenderPortal>
                            }
                        />
                        <Route
                            path={routeConstants.notFound}
                            element={
                                <PageNotFound />
                            }
                        />
                        <Route
                            path={routeConstants.tenantLenderDisabled}
                            element={
                                <BasicStrongboxContentContainer
                                    flow={'lender'}
                                >
                                    <TenantDisabled
                                        flow={'lender'}
                                    />
                                </BasicStrongboxContentContainer>
                            }
                        />
                        <Route
                            path={routeConstants.tenantBorrowerDisabled}
                            element={
                                <BasicStrongboxContentContainer
                                    flow={'borrower'}
                                >
                                    <TenantDisabled
                                        flow={'borrower'}
                                    />
                                </BasicStrongboxContentContainer>
                            }
                        />
                        <Route path={routeConstants.settings}>
                            <Route path={''} element={<Navigate to={pathConstants.settingsDefault} />} />
                            <Route
                                path={routeConstants.settingsUserManagement}
                                element={
                                    <AuthorizedWindowWrapper
                                        securedOp={'CanManageUsers'}
                                        mainDivKey={mainDivKey.toString()}
                                        onPostLoginComplete={onPostLoginComplete}
                                    >
                                        <LenderPortal>
                                            <SettingsContainer />
                                        </LenderPortal>
                                    </AuthorizedWindowWrapper>
                                }
                            />
                            <Route
                                path={routeConstants.settingsNotifications}
                                element={
                                    <LenderPortal>
                                        <UserSettingsContainer
                                            subPage={UserSettingsSubPage.notifications}
                                        />
                                    </LenderPortal>
                                }
                            />
                            <Route
                                path={routeConstants.settingsAdminNotifications}
                                element={
                                    <LenderPortal>
                                        <UserSettingsContainer
                                            subPage={UserSettingsSubPage.admin}
                                        />
                                    </LenderPortal>
                                }
                            />
                        </Route>

                        <Route
                            path={routeConstants.tenant}
                            element={
                                <AuthorizedWindowWrapper
                                    mainDivKey={mainDivKey.toString()}
                                    onPostLoginComplete={onPostLoginComplete}
                                >
                                    <LenderPortal>
                                        <TenantSelection />
                                    </LenderPortal>
                                </AuthorizedWindowWrapper>
                            }
                        />
                        <Route
                            path={routeConstants.brand}
                            element={
                                <AuthorizedWindowWrapper
                                    mainDivKey={mainDivKey.toString()}
                                    onPostLoginComplete={onPostLoginComplete}
                                >
                                    <LenderPortal>
                                        <TenantSelection />
                                    </LenderPortal>
                                </AuthorizedWindowWrapper>
                            }
                        />
                        <Route
                            path={pathConstants.portal}
                            element={
                                <BorrowerPortalV2
                                    showTOU={true}
                                    scrollableContent={true}
                                >
                                    {(props): JSX.Element => {
                                        return (
                                            <PortalSubmissionV2
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.submission}
                            element={
                                <BorrowerPortalV2
                                    showTOU={true}
                                    scrollableContent={true}
                                >
                                    {(props): JSX.Element => {
                                        return (
                                            <PortalSubmissionV2
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.submissionSansFileUpload}
                            element={
                                <BorrowerPortalV2
                                    showTOU={true}
                                    scrollableContent={true}
                                >
                                    {(props): JSX.Element => {
                                        return (
                                            <PortalSubmissionV2
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.progress}
                            element={
                                <BorrowerPortalV2>
                                    {(props): JSX.Element => {
                                        return (
                                            <Progress
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.linked}
                            element={
                                <BorrowerPortalV2
                                    scrollableContent={true}
                                >
                                    {(props): JSX.Element => {
                                        return (
                                            <Linked
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.documents}
                            element={
                                <BorrowerPortalV2>
                                    {(props): JSX.Element => {
                                        return (
                                            <Documents
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.shareableLinkExpired}
                            element={
                                <BorrowerPortalV2
                                    showTOU={false}
                                >
                                    {(props): JSX.Element => {
                                        return (
                                            <ShareableLinkExpired
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.shareableLinkDeleted}
                            element={
                                <BorrowerPortalV2
                                    showTOU={false}
                                >
                                    {(props): JSX.Element => {
                                        return (
                                            <ShareableLinkDeleted
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                scrollTop={props.scrollTop}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                        <Route
                            path={routeConstants.confirm}
                            element={
                                <EmailConfirm />
                            }
                        />
                        <Route
                            path={routeConstants.confirming}
                            element={
                                <EmailConfirm delayConfirm={true} />
                            }
                        />
                        <Route
                            path={routeConstants.downloadUserCopy}
                            element={
                                <BorrowerPortalV2>
                                    {(props): JSX.Element => {
                                        return (
                                            <DownloadUserCopy
                                                borrowerId={props.borrowerId}
                                                setIsWorking={props.setIsWorking}
                                                setOverrideWorking={props.setOverrideWorking}
                                                strongboxUrl={props.strongboxUrl}
                                                submissionId={props.submissionId}
                                                welcome={props.welcome}
                                                working={props.working}
                                                importAccountingPkgAnonymous={props.importAccountingPkgAnonymous}
                                                importAccountingPkg={props.importAccountingPkg}
                                                tenantName={props.tenantName}
                                            />
                                        )
                                    }}
                                </BorrowerPortalV2>
                            }
                        />
                    </Routes>
                </Wrapper>
            </BrowserRouter>
        </LoggingWrapper>
    );
}

export default connect<ReduxStateProps, ReduxDispatchProps, PropArguments, ApplicationState>(
    // map state to props
    (state: ApplicationState) => {
        return {
            appSettings: state.appSettings,
            tenantSettings: GetBasicSettings(state),
            importHasStarted: ImportHasStarted(state),
            tenantName: GetTenantName(state),
            userIsAuthenticated: GetUserIsAuthenticated(state),
        };
    },
    // map dispatch to props
    dispatch => bindActionCreators(
        {
            ...AppSettings.actionCreators,
            ...UserActions,
            ...BorrowerActionCreators,
            ...TenantActionCreators,
            ...PortalAssetsActionCreators,
        },
        dispatch
    )
)(App);
